import type { CurrentUser } from '@btsis/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, type ReactNode, use, useCallback, useEffect, useMemo } from 'react';
import { api } from './api.js';

export type LoginResult =
  | { status: 'success' }
  | { status: 'two_factor_required'; challengeToken: string };

type AuthContextValue = {
  user: CurrentUser | null;
  isLoading: boolean;
  login: (input: { email: string; password: string }) => Promise<LoginResult>;
  verifyTwoFactor: (input: { challengeToken: string; code: string }) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  impersonate: (userId: string) => Promise<void>;
  stopImpersonation: () => Promise<void>;
  can: (permission: string) => boolean;
  /**
   * `can`, or a campaign staff assignment in force derives the permission
   * (`staffPermissions`, docs/decisions/0090). Only the fulfilment screens ask
   * it — the list and page of a donation, its queue, fulfil and print buttons —
   * because the server honours a derived permission on those routes alone and
   * only for the donations the assignment reaches. Everything else asks `can`.
   */
  canWork: (permission: string) => boolean;
  /**
   * A campaign staff assignment in force derives the permission, whatever the
   * roles hold. For a screen a role reaches by a permission of its own and an
   * assignment by a derived one — *Gelen Medya*, `inboundMedia.list` for a
   * role and `donation.fulfill` for an assignment (docs/decisions/0093).
   */
  staffMay: (permission: string) => boolean;
  /** An assignment is in force: the session carries derived fulfilment work. */
  staffed: boolean;
  /**
   * The session reads an organization's money at all — false for a `self`
   * scope, which holds no organization (docs/decisions/0087). The server
   * refuses every organization read to it regardless; this keeps the menu and
   * the routes from offering a screen that would load empty or refused.
   */
  readsOrganizations: boolean;
};

const SESSION_TAB_ID = crypto.randomUUID();

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      try {
        const result = await api.auth.me({});
        return result.user;
      } catch {
        return null;
      }
    },
    retry: false,
    staleTime: 60_000,
    refetchInterval: (query) => (query.state.data?.impersonation ? 30_000 : false),
    refetchOnWindowFocus: 'always',
  });

  const loginMutation = useMutation({
    mutationFn: (input: { email: string; password: string }) => api.auth.login(input),
    onSuccess: (result) => {
      // A two-factor challenge is not a session yet, so nothing is cached.
      if (result.status === 'success') {
        queryClient.setQueryData(['auth', 'me'], result.user);
      }
    },
  });

  const twoFactorMutation = useMutation({
    mutationFn: (input: { challengeToken: string; code: string }) =>
      api.auth.verifyTwoFactor(input),
    onSuccess: (result) => {
      queryClient.setQueryData(['auth', 'me'], result.user);
    },
  });

  const logoutMutation = useMutation({
    mutationFn: () => api.auth.logout({}),
    onSuccess: () => {
      queryClient.setQueryData(['auth', 'me'], null);
      void queryClient.invalidateQueries();
    },
  });

  const user = data ?? null;
  const accessBlocked = Boolean(
    user?.impersonation &&
      (user.mustEnrolTwoFactor || Date.parse(user.impersonation.expiresAt) <= Date.now()),
  );

  useEffect(() => {
    const channel = new BroadcastChannel('btsis-session');
    channel.onmessage = (event) => {
      if (event.data?.tabId !== SESSION_TAB_ID) window.location.assign('/');
    };
    return () => channel.close();
  }, []);

  const changeIdentity = useCallback(
    async (run: () => Promise<unknown>, path: string) => {
      await queryClient.cancelQueries();
      await run();
      queryClient.clear();
      const channel = new BroadcastChannel('btsis-session');
      channel.postMessage({ tabId: SESSION_TAB_ID });
      channel.close();
      window.location.assign(path);
    },
    [queryClient],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      login: async (input) => {
        const result = await loginMutation.mutateAsync(input);
        return result.status === 'success'
          ? { status: 'success' }
          : { status: 'two_factor_required', challengeToken: result.challengeToken };
      },
      verifyTwoFactor: async (input) => {
        await twoFactorMutation.mutateAsync(input);
      },
      logout: async () => {
        await logoutMutation.mutateAsync();
      },
      refresh: async () => {
        await queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
      },
      impersonate: (userId) => changeIdentity(() => api.auth.impersonation.start({ userId }), '/'),
      stopImpersonation: () => changeIdentity(() => api.auth.impersonation.stop({}), '/users'),
      can: (permission) => !accessBlocked && (user?.permissions.includes(permission) ?? false),
      canWork: (permission) =>
        !accessBlocked &&
        ((user?.permissions.includes(permission) ?? false) ||
          (user?.staffPermissions.includes(permission) ?? false)),
      staffMay: (permission) =>
        !accessBlocked && (user?.staffPermissions.includes(permission) ?? false),
      staffed: !accessBlocked && (user?.staffPermissions.length ?? 0) > 0,
      readsOrganizations: !accessBlocked && user !== null && user.scope.level !== 'self',
    }),
    [
      user,
      isLoading,
      loginMutation,
      twoFactorMutation,
      logoutMutation,
      queryClient,
      changeIdentity,
      accessBlocked,
    ],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthContextValue {
  const context = use(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}
